import { useEffect, type ComponentType } from 'react'
import {
  screenById,
  startAuthenticatedScreen,
  startScreen,
  type InterstitialScreenId,
  type RoutableScreenId,
} from '@/lib/contract'
import { OriginProvider } from '@/lib/origin'
import { useNavigate, useRoute } from '@/lib/router'
import { SessionProvider, useSession } from '@/lib/session'

import { AccountDetailScreen } from '@/screens/accountDetail'
import { AuthCollisionScreen } from '@/screens/authCollision'
import { BetaAccessScreen } from '@/screens/betaAccess'
import { AccountsScreen } from '@/screens/accounts'
import { CashFlowScreen } from '@/screens/cashFlow'
import { CategoriesScreen } from '@/screens/categories'
import { CategoryDetailScreen } from '@/screens/categoryDetail'
import { ConnectedAccountsScreen } from '@/screens/connectedAccounts'
import { DashboardScreen } from '@/screens/dashboard'
import { DebtPlannerScreen } from '@/screens/debtPlanner'
import { DebtScreen } from '@/screens/debt'
import { ExportScreen } from '@/screens/export'
import { FeesScreen } from '@/screens/fees'
import { HealthScoreScreen } from '@/screens/healthScore'
import { ImportReviewScreen } from '@/screens/importReview'
import { LinkAccountScreen } from '@/screens/linkAccount'
import { LinkErrorScreen } from '@/screens/linkError'
import { LinkSuccessScreen } from '@/screens/linkSuccess'
import { PrivacyScreen } from '@/screens/privacy'
import { ProgressScreen } from '@/screens/progress'
import { RecommendationDetailScreen } from '@/screens/recommendationDetail'
import { RecommendationsScreen } from '@/screens/recommendations'
import { ReauthenticateScreen } from '@/screens/reauthenticate'
import { RecurringDetailScreen } from '@/screens/recurringDetail'
import { RecurringScreen } from '@/screens/recurring'
import { SavingsScreen } from '@/screens/savings'
import { SettingsScreen } from '@/screens/settings'
import { ServiceUnavailableScreen } from '@/screens/serviceUnavailable'
import { SignInScreen } from '@/screens/signIn'
import { SignInMethodsScreen } from '@/screens/signInMethods'
import { TransactionDetailScreen } from '@/screens/transactionDetail'
import { TransactionsScreen } from '@/screens/transactions'
import { UploadScreen } from '@/screens/upload'
import { WelcomeScreen } from '@/screens/welcome'

/**
 * Every routable screen in product/screens.json, by id. RoutableScreenId is
 * generated from the contract, so a screen added by another surface fails to
 * compile here until Web implements it — which is the point.
 */
const SCREENS: Record<RoutableScreenId, ComponentType> = {
  welcome: WelcomeScreen,
  signIn: SignInScreen,
  authCollision: AuthCollisionScreen,
  betaAccess: BetaAccessScreen,
  dashboard: DashboardScreen,
  upload: UploadScreen,
  importReview: ImportReviewScreen,
  accounts: AccountsScreen,
  accountDetail: AccountDetailScreen,
  transactions: TransactionsScreen,
  transactionDetail: TransactionDetailScreen,
  categories: CategoriesScreen,
  categoryDetail: CategoryDetailScreen,
  recurring: RecurringScreen,
  recurringDetail: RecurringDetailScreen,
  fees: FeesScreen,
  cashFlow: CashFlowScreen,
  debt: DebtScreen,
  debtPlanner: DebtPlannerScreen,
  savings: SavingsScreen,
  healthScore: HealthScoreScreen,
  recommendations: RecommendationsScreen,
  recommendationDetail: RecommendationDetailScreen,
  progress: ProgressScreen,
  settings: SettingsScreen,
  signInMethods: SignInMethodsScreen,
  reauthenticate: ReauthenticateScreen,
  export: ExportScreen,
  privacy: PrivacyScreen,
  linkAccount: LinkAccountScreen,
  linkSuccess: LinkSuccessScreen,
  linkError: LinkErrorScreen,
  connectedAccounts: ConnectedAccountsScreen,
}

/**
 * Screens the contract marks `interstitial`: no route, no URL, presented OVER
 * whichever screen is already open. They are registered here for the same
 * reason the routable ones are — this file is the single place an id maps to a
 * component — but they are handed to whatever presents them rather than to the
 * router, which has no address for them.
 */
const INTERSTITIALS: Record<InterstitialScreenId, ComponentType> = {
  serviceUnavailable: ServiceUnavailableScreen,
}

function Router() {
  const route = useRoute()
  const navigate = useNavigate()
  const { authenticated } = useSession()

  const spec = screenById.get(route.screen)
  const needsAuth = spec?.auth === 'required'

  // Land on the right start screen, and never render an auth-required screen
  // without a session.
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '') {
      navigate(authenticated ? startAuthenticatedScreen : startScreen, {}, { replace: true })
      return
    }
    if (needsAuth && !authenticated) {
      navigate('signIn', {}, { replace: true })
    }
  }, [authenticated, needsAuth, navigate, route.screen])

  if (needsAuth && !authenticated) return null

  const ScreenComponent = SCREENS[route.screen]
  return <ScreenComponent />
}

export function App() {
  return (
    <SessionProvider>
      <OriginProvider screens={INTERSTITIALS}>
        <Router />
      </OriginProvider>
    </SessionProvider>
  )
}
